import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import TableToggle from '../payment/TableToggle';
import MarqueeEditModal from './MarqueeEditModal';
import {
  MARQUEE_STATE,
  MARQUEE_TABS,
  MARQUEE_TYPE,
  buildMarqueePageParams,
  formatMarqueeState,
} from './marqueeConstants';

const TABLE_COLUMNS = ['標題（英語）', '創建時間', '狀態', '順序', '操作'];

export default function MarqueePage() {
  const [activeTab, setActiveTab] = useState(MARQUEE_TYPE.GENERAL);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [dynamicSwitchOn, setDynamicSwitchOn] = useState(false);
  const [switchLoading, setSwitchLoading] = useState(false);
  const [switchToggling, setSwitchToggling] = useState(false);

  const [editOpen, setEditOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  async function loadDynamicSwitch() {
    setSwitchLoading(true);
    try {
      const data = await adminApi.getDynamicMarqueeSwitch();
      setDynamicSwitchOn(Number(data?.enableDynamicMarquee) === 1);
    } catch {
      setDynamicSwitchOn(false);
    } finally {
      setSwitchLoading(false);
    }
  }

  async function loadRecords(pageNo = 1, marqueeType = activeTab) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.marqueePageQuery(buildMarqueePageParams(pageNo, marqueeType));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadDynamicSwitch();
  }, []);

  useEffect(() => {
    loadRecords(1, activeTab);
  }, [activeTab]);

  function openCreate() {
    setEditId(null);
    setEditOpen(true);
    setMessage('');
  }

  function openEdit(row) {
    setEditId(row.id);
    setEditOpen(true);
    setMessage('');
  }

  function handleSaved(isEdit) {
    setMessage(isEdit ? '修改成功' : '新增成功');
    loadRecords(page, activeTab);
  }

  async function handleDynamicSwitchChange(checked) {
    setSwitchToggling(true);
    setError('');
    try {
      await adminApi.setDynamicMarqueeSwitch({ enableDynamicMarquee: checked ? 1 : 0 });
      setDynamicSwitchOn(checked);
      setMessage(checked ? '動態跑馬燈已啟用' : '動態跑馬燈已關閉');
    } catch (err) {
      setError(err.message || '開關設定失敗');
    } finally {
      setSwitchToggling(false);
    }
  }

  async function confirmDelete() {
    if (!deleteTarget?.id) {
      return;
    }
    setDeleting(true);
    setError('');
    try {
      await adminApi.deleteMarquee(deleteTarget.id);
      setMessage('刪除成功');
      setDeleteTarget(null);
      await loadRecords(page, activeTab);
    } catch (err) {
      setError(err.message || '刪除失敗');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>跑馬燈</h2>
      </div>

      <nav className="product-tabs" aria-label="跑馬燈類型">
        {MARQUEE_TABS.map((tab) => (
          <button
            key={tab.key}
            type="button"
            className={`product-tabs__item ${activeTab === tab.key ? 'is-active' : ''}`}
            onClick={() => setActiveTab(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </nav>

      <div className="section-header section-header--compact">
        <div className="section-header__actions">
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            新增
          </button>
        </div>
      </div>

      {activeTab === MARQUEE_TYPE.DYNAMIC ? (
        <div className="global-switch-bar">
          <span className="switch-field">動態跑馬燈開關</span>
          <TableToggle
            checked={dynamicSwitchOn}
            disabled={switchLoading || switchToggling}
            ariaLabel="動態跑馬燈開關"
            onChange={handleDynamicSwitchChange}
          />
        </div>
      ) : null}

      {message ? <p className="form-message form-message--success">{message}</p> : null}
      {error ? <p className="form-message form-message--error">{error}</p> : null}

      <div className="table-meta">共 {total} 筆</div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  載入中…
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  無資料
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td>{row.enTitle || '未設定'}</td>
                  <td>{formatDateTime(row.createTime)}</td>
                  <td>
                    <span className={row.state === MARQUEE_STATE.ON ? 'status-text status-text--success' : 'status-text'}>
                      {formatMarqueeState(row.state)}
                    </span>
                  </td>
                  <td>{row.sort ?? '—'}</td>
                  <td>
                    <div className="table-actions">
                      <button type="button" className="btn btn-success btn-sm" onClick={() => openEdit(row)}>
                        修改
                      </button>
                      <button
                        type="button"
                        className="btn btn-danger btn-sm"
                        onClick={() => setDeleteTarget(row)}
                      >
                        刪除
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(next) => loadRecords(next, activeTab)} />

      <MarqueeEditModal
        open={editOpen}
        marqueeId={editId}
        marqueeType={activeTab}
        onClose={() => setEditOpen(false)}
        onSaved={handleSaved}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="刪除跑馬燈"
        message="確定刪除此跑馬燈？"
        confirmLabel={deleting ? '刪除中…' : '刪除'}
        confirmDisabled={deleting}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />
    </div>
  );
}
